import React, { useEffect, useState, useCallback } from 'react';
import {
  Row,
  Col,
  Typography,
  Space,
  Button,
  message,
  Spin,
  Empty,
  Card,
  Table,
  Modal,
  Form,
  InputNumber,
  Select,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  ReloadOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { PricingService, PricingPlan, Invoice } from '../services/pricingService';
import { BrandService } from '../services/brandService';
import { Brand } from '../types';
import InvoiceViewer from '../components/pricing/InvoiceViewer';

const { Title, Text } = Typography;
const { Option } = Select;

const FeaturePricing: React.FC = () => {
  const { user } = useAuth();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Modal states
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PricingPlan | null>(null);
  const [form] = Form.useForm();
  
  // Invoice modal
  const [invoiceModalVisible, setInvoiceModalVisible] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [invoiceMonth, setInvoiceMonth] = useState<string>('');

  const isAdmin = user?.role === 'admin';

  const loadBrands = useCallback(async () => {
    try {
      const fetchedBrands = await BrandService.getAllBrands();
      setBrands(fetchedBrands);
      if (fetchedBrands.length > 0) {
        setSelectedBrandId(fetchedBrands[0].id);
      }
    } catch (error) {
      console.error('Failed to load brands:', error);
      message.error('Markalar yüklenemedi');
    }
  }, []);

  const loadPricingPlans = useCallback(async () => {
    if (!selectedBrandId) return;

    try {
      setLoading(true);
      const plans = await PricingService.getPricingPlans(selectedBrandId);
      setPricingPlans(plans);
    } catch (error) {
      console.error('Failed to load pricing plans:', error);
      message.error('Fiyatlandırma planları yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [selectedBrandId]);

  useEffect(() => {
    if (user && isAdmin) {
      loadBrands();
    }
  }, [user, isAdmin, loadBrands]);

  useEffect(() => {
    if (selectedBrandId) {
      loadPricingPlans();
    }
  }, [selectedBrandId, loadPricingPlans]);

  const handleCreatePlan = () => {
    setEditingPlan(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditPlan = (plan: PricingPlan) => {
    setEditingPlan(plan);
    form.setFieldsValue({
      featureCode: plan.featureCode,
      baseQuota: plan.baseQuota,
      basePrice: plan.basePrice,
      overageUnit: plan.overageUnit,
      overagePrice: plan.overagePrice,
      currency: plan.currency,
    });
    setModalVisible(true);
  };

  const handleSavePlan = async () => {
    if (!selectedBrandId) return;

    try {
      const values = await form.validateFields();
      await PricingService.updatePricingPlan(selectedBrandId, values.featureCode, values);
      message.success('Fiyatlandırma planı kaydedildi');
      setModalVisible(false);
      form.resetFields();
      loadPricingPlans();
    } catch (error) {
      console.error('Failed to save pricing plan:', error);
      message.error('Plan kaydedilemedi');
    }
  };

  const handleViewInvoice = async (month: string) => {
    if (!selectedBrandId) return;

    try {
      const invoice = await PricingService.getInvoice(selectedBrandId, month);
      setSelectedInvoice(invoice);
      setInvoiceMonth(month);
      setInvoiceModalVisible(true);
    } catch (error) {
      console.error('Failed to load invoice:', error);
      message.error('Fatura yüklenemedi');
    }
  };

  const handleExportInvoiceCSV = async () => {
    if (!selectedBrandId || !invoiceMonth) return;

    try {
      const blob = await PricingService.exportInvoiceCSV(selectedBrandId, invoiceMonth);
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${selectedBrandId}-${invoiceMonth}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      message.success('Fatura CSV olarak indirildi');
    } catch (error) {
      console.error('Failed to export invoice:', error);
      message.error('Fatura indirilemedi');
    }
  };

  const handleExportInvoicePDF = async () => {
    if (!selectedBrandId || !invoiceMonth) return;

    try {
      const blob = await PricingService.exportInvoicePDF(selectedBrandId, invoiceMonth);
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${selectedBrandId}-${invoiceMonth}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      message.success('Fatura indirildi');
    } catch (error) {
      console.error('Failed to export invoice:', error);
      message.error('Fatura indirilemedi');
    }
  };

  if (!isAdmin) {
    return <Empty description="Bu sayfa sadece admin kullanıcılar için erişilebilir" />;
  }

  const columns = [
    {
      title: 'Feature',
      dataIndex: 'featureName',
      key: 'featureName',
    },
    {
      title: 'Temel Kota',
      dataIndex: 'baseQuota',
      key: 'baseQuota',
      align: 'right' as const,
    },
    {
      title: 'Temel Fiyat',
      dataIndex: 'basePrice',
      key: 'basePrice',
      align: 'right' as const,
      render: (value: number, record: PricingPlan) => (
        <Text>{value} {record.currency}</Text>
      ),
    },
    {
      title: 'Aşım Birimi',
      dataIndex: 'overageUnit',
      key: 'overageUnit',
      align: 'right' as const,
    },
    {
      title: 'Aşım Fiyatı',
      dataIndex: 'overagePrice',
      key: 'overagePrice',
      align: 'right' as const,
      render: (value: number, record: PricingPlan) => (
        <Text>{value} {record.currency}</Text>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'Aktif' : 'Pasif'}
        </Tag>
      ),
    },
    {
      title: 'İşlem',
      key: 'action',
      render: (_: any, record: PricingPlan) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditPlan(record)}
        >
          Düzenle
        </Button>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Feature Fiyatlandırma Yönetimi</Title>
        <Text type="secondary">
          Marka bazlı feature fiyatlandırma planlarını yönetin ve faturaları görüntüleyin
        </Text>
      </div>

      {/* Brand Selection */}
      <Card style={{ marginBottom: 16 }}>
        <Space size="middle" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Text strong>Marka:</Text>
            <Select
              style={{ width: 300 }}
              value={selectedBrandId}
              onChange={setSelectedBrandId}
              placeholder="Marka seçin"
            >
              {brands.map(brand => (
                <Option key={brand.id} value={brand.id}>
                  {brand.name}
                </Option>
              ))}
            </Select>
          </Space>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreatePlan}
              disabled={!selectedBrandId}
            >
              Yeni Plan Ekle
            </Button>
            <Button
              icon={<FileTextOutlined />}
              onClick={() => {
                const currentMonth = new Date().toISOString().split('T')[0].substring(0, 8) + '01';
                handleViewInvoice(currentMonth);
              }}
              disabled={!selectedBrandId}
            >
              Mevcut Ay Faturası
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadPricingPlans}
              loading={loading}
            >
              Yenile
            </Button>
          </Space>
        </Space>
      </Card>

      {/* Pricing Plans Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size="large" />
        </div>
      ) : !selectedBrandId ? (
        <Empty description="Lütfen bir marka seçin" />
      ) : (
        <Card>
          <Table
            dataSource={pricingPlans}
            columns={columns}
            rowKey="id"
            pagination={false}
          />
        </Card>
      )}

      {/* Plan Edit Modal */}
      <Modal
        title={editingPlan ? 'Planı Düzenle' : 'Yeni Plan Ekle'}
        open={modalVisible}
        onOk={handleSavePlan}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Feature Code"
            name="featureCode"
            rules={[{ required: true, message: 'Feature code gerekli' }]}
          >
            <Select placeholder="Feature seçin" disabled={!!editingPlan}>
              <Option value="basket_combinations">Sepet Kombinasyonları</Option>
              <Option value="ai_recommendations">AI Ürün Önerileri</Option>
              <Option value="product_analytics">Ürün Analitiği</Option>
              <Option value="style_advisor">Stil Danışmanı</Option>
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Temel Kota"
                name="baseQuota"
                rules={[{ required: true, message: 'Temel kota gerekli' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  placeholder="1000"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Temel Fiyat"
                name="basePrice"
                rules={[{ required: true, message: 'Temel fiyat gerekli' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  placeholder="300.00"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Aşım Birimi"
                name="overageUnit"
                rules={[{ required: true, message: 'Aşım birimi gerekli' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={1}
                  placeholder="100"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Aşım Fiyatı"
                name="overagePrice"
                rules={[{ required: true, message: 'Aşım fiyatı gerekli' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  placeholder="10.00"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Para Birimi"
            name="currency"
            rules={[{ required: true, message: 'Para birimi gerekli' }]}
            initialValue="USD"
          >
            <Select>
              <Option value="USD">USD</Option>
              <Option value="EUR">EUR</Option>
              <Option value="TRY">TRY</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Invoice Modal */}
      <Modal
        title="Aylık Fatura"
        open={invoiceModalVisible}
        onCancel={() => {
          setInvoiceModalVisible(false);
          setSelectedInvoice(null);
        }}
        footer={null}
        width={1000}
      >
        {selectedInvoice && (
          <InvoiceViewer
            invoice={selectedInvoice}
            onExportPDF={handleExportInvoicePDF}
            onExportCSV={handleExportInvoiceCSV}
          />
        )}
      </Modal>
    </div>
  );
};

export default FeaturePricing;

